import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { useSession } from '@/state/session';

/**
 * Hydrates the session store from /api/me. Critical correctness rules:
 *
 *   1. We always refetch on mount (`refetchOnMount: 'always'`) so stale
 *      cross-page cache (e.g. an anonymous `/api/me` from Home prior to
 *      login) doesn't get applied to a fresh /me page.
 *
 *   2. We only call setUser AFTER a fetch that completed during *this*
 *      hook's lifetime. Cached values from before mount are ignored.
 *      This guarantees the session store never flips to 'anonymous'
 *      because of pre-mount cache.
 */
export function useMeBootstrap() {
  const setUser = useSession((s) => s.setUser);
  const mountedAt = useRef<number>(Date.now());
  const lastApplied = useRef<number>(0);

  const query = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
    staleTime: 30_000,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    // Reject pre-mount cache hits.
    if (query.dataUpdatedAt < mountedAt.current) return;
    if (query.dataUpdatedAt === lastApplied.current) return;
    lastApplied.current = query.dataUpdatedAt;
    setUser(query.data?.user ?? null);
  }, [query.isSuccess, query.dataUpdatedAt, query.data, setUser]);

  return query;
}
